import BookmarkAddOutlinedIcon from "@mui/icons-material/BookmarkAddOutlined";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import { useSnackbar } from "_shared/_providers/snackbars/snackbars";
import { MarketplaceSearchRequestJSON } from "marketplace/_schema/marketplace-search-request-schema";
import { useSaveSearchMutation } from "marketplace/saved-searches/_api/saved-search-api";
import SavedSearchSchema from "marketplace/saved-searches/_schema/saved-search-schema";
import { savedSearchFailureText } from "marketplace/saved-searches/_views/saved-search-failure-text";
import React from "react";

export interface SaveSearchButtonProps {
  /** The search on screen. */
  request: MarketplaceSearchRequestJSON;
  /** What the dialog suggests calling it. */
  suggestedName: string;
}

/** Saves the marketplace search on screen under a name the user chooses. */
export default function SaveSearchButton(props: Readonly<SaveSearchButtonProps>) {
  const { request, suggestedName } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [saveSearch, { isLoading }] = useSaveSearchMutation();
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState("");
  const [alertsEnabled, setAlertsEnabled] = React.useState(true);

  const trimmedName = name.trim();
  const nameTooLong = trimmedName.length > SavedSearchSchema.MaximumNameLength;

  function openDialog() {
    setName(suggestedName.slice(0, SavedSearchSchema.MaximumNameLength));
    setAlertsEnabled(true);
    setOpen(true);
  }

  async function handleSave(event: React.FormEvent) {
    event.preventDefault();
    if (!trimmedName || nameTooLong) return;

    const saved = await saveSearch({ name: trimmedName, alertsEnabled, criteria: request })
      .unwrap()
      .then(() => true)
      .catch((error: unknown) => {
        enqueueSnackbar(savedSearchFailureText(error, "Couldn't save the search. Please try again."), {
          variant: "error",
        });
        return false;
      });
    if (!saved) return;

    setOpen(false);
    enqueueSnackbar(
      alertsEnabled ? "Saved. We'll tell you when new profiles match." : "Saved search",
      { variant: "success" },
    );
  }

  return (
    <>
      <Button size="large" color="primary" startIcon={<BookmarkAddOutlinedIcon />} onClick={openDialog}>
        Save Search
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <form onSubmit={handleSave} noValidate>
          <DialogTitle>Save this search</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="dense"
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              error={nameTooLong}
              helperText={
                nameTooLong ? `Use ${SavedSearchSchema.MaximumNameLength} characters or fewer.` : undefined
              }
              required
            />
            <FormControlLabel
              control={
                <Switch checked={alertsEnabled} onChange={(event) => setAlertsEnabled(event.target.checked)} />
              }
              label="Tell me when new profiles match"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={!trimmedName || nameTooLong || isLoading}>
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
